import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        {/* Back link */}
        <Link to="/" className="back-link" data-testid="back-to-home">
          <ArrowLeft size={18} />
          <span>Back to Calorie Tracker</span>
        </Link>

        {/* Header */}
        <header className="privacy-header">
          <h1>Privacy Policy</h1>
          <div className="privacy-dates">
            <p><strong>Created date:</strong> 27.01.2026</p>
            <p><strong>Last updated:</strong> 27.01.2026</p>
          </div>
        </header>

        {/* Intro */}
        <section className="privacy-section">
          <p>
            This Privacy Policy explains how <strong>Mariusz Jankowski</strong> ("Developer") collects, uses, and shares information when you use the <strong>Calorie Tracker</strong> iOS app (the "App").
          </p>
          <p>
            If you have questions, contact us at: <a href="mailto:support@smartkcal.app">support@smartkcal.app</a>
          </p>
        </section>

        {/* Section 1 */}
        <section className="privacy-section">
          <h2>1. Key points (summary)</h2>
          <ul>
            <li>The App stores your data <strong>locally on your device</strong> and does not require an account.</li>
            <li>We do not store your meal and progress history on our servers.</li>
            <li>If you use cloud-powered features (AI meal analysis, cloud transcription, nutrition lookups), the App sends the information required to process your request.</li>
            <li>We <strong>do not sell</strong> your personal information and we do not run third‑party advertising tracking SDKs in the App.</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="privacy-section">
          <h2>2. Information we collect</h2>
          
          <h3>2.1 Data you provide in the App</h3>
          <p>Depending on the features you use, you may provide:</p>
          <ul>
            <li><strong>Meal information:</strong> meal descriptions you type or dictate, food items, and calorie estimates.</li>
            <li><strong>Profile and goals:</strong> daily calorie goal and optional body basics (e.g., age, height).</li>
            <li><strong>Progress data:</strong> weight and body measurements.</li>
            <li><strong>Progress photos:</strong> photos you choose to store in the App for progress tracking.</li>
          </ul>

          <h3>2.2 Audio recordings (microphone)</h3>
          <p>If you choose voice input, the App records short <strong>audio clips</strong> to transcribe your meal description.</p>
          <ul>
            <li>When available, we use a <strong>cloud transcription provider</strong> via our backend.</li>
            <li>If cloud transcription is unavailable, the App may fall back to <strong>Apple Speech Recognition</strong>.</li>
          </ul>

          <h3>2.3 Photos and image processing</h3>
          <p>The App can use images for two separate purposes:</p>
          <ul>
            <li><strong>Nutrition label scanning (OCR):</strong> you can take a photo of a nutrition label or choose an image, then the App performs on‑device OCR to extract text.</li>
            <li><strong>Progress photos:</strong> you can save photos in the App for progress comparisons.</li>
          </ul>

          <h3>2.4 Subscription and purchase information</h3>
          <p>If you purchase a subscription, Apple processes the transaction. We may receive:</p>
          <ul>
            <li><strong>Entitlement status</strong> (active/inactive),</li>
            <li><strong>Product identifiers</strong> and basic transaction metadata needed to unlock features.</li>
          </ul>
          <p>We do <strong>not</strong> receive your full payment card details.</p>

          <h3>2.5 Device, security, and diagnostics data</h3>
          <p>To protect the service and ensure reliability, we may collect:</p>
          <ul>
            <li><strong>IP address</strong>, timestamps, and request metadata,</li>
            <li><strong>Device and app information</strong> (e.g., iOS version, app version, language/locale),</li>
            <li><strong>Security identifiers</strong> used to authenticate requests (e.g., a public key registered from your device; the private key remains on your device).</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="privacy-section">
          <h2>3. How we use information</h2>
          <p>We use the information to:</p>
          <ul>
            <li>Provide core features (meal logging, meal planning, progress tracking).</li>
            <li>Run AI features you request (e.g., meal analysis, nutrition label interpretation, translation of food names for database lookups).</li>
            <li>Transcribe voice input into text.</li>
            <li>Generate supportive weekly insights/motivation messages based on aggregated in‑app metrics.</li>
            <li>Prevent abuse, enforce rate limits, and secure the backend.</li>
            <li>Provide support and respond to inquiries.</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="privacy-section">
          <h2>4. How we share information</h2>
          <p>We share information only as needed to provide the App and comply with law.</p>

          <h3>4.1 Feature-based data flows (what is sent where)</h3>
          <p>Depending on which feature you use, the App may transmit the following data off your device:</p>
          <ul>
            <li><strong>Voice transcription (third-party speech-to-text via our backend):</strong> an audio clip you record, its MIME type, and your selected language are sent to our backend servers, which forward the audio to a third-party speech-to-text provider to return a transcript.</li>
            <li><strong>Voice transcription fallback (Apple Speech Recognition):</strong> if cloud transcription is unavailable, the audio clip may be sent to <strong>Apple</strong> for speech recognition.</li>
            <li><strong>AI meal analysis / label interpretation / weekly messages (third-party AI processing):</strong> the text you enter or dictate (including OCR‑extracted label text) is sent to our backend servers, which forward it to a third-party AI processing provider to generate structured results.</li>
            <li><strong>Nutrition lookups (nutrition databases via our backend):</strong> your food search query is sent to our backend servers, which query third-party nutrition databases to return calorie information.</li>
          </ul>

          <h3>4.2 Service providers (processors)</h3>
          <p>Depending on your actions in the App, data may be processed by:</p>
          <ul>
            <li><strong>Infrastructure and security providers</strong> to deliver and protect our backend.</li>
            <li><strong>AI processing provider</strong> to generate structured meal analysis, interpret OCR text, and produce weekly messages.</li>
            <li><strong>Speech-to-text provider</strong> to transcribe audio clips when cloud transcription is used.</li>
            <li><strong>Nutrition database providers</strong> used via our backend to look up calorie information from your search terms.</li>
            <li><strong>Apple</strong> (StoreKit) to process subscriptions and manage purchases.</li>
          </ul>
          <p>We can provide a current list of our third‑party subprocessors upon request at <a href="mailto:support@smartkcal.app">support@smartkcal.app</a>.</p>

          <h3>4.3 Legal and safety</h3>
          <p>We may disclose information if required by law or to protect users, our service, and our rights.</p>

          <h3>4.4 No sale of personal information</h3>
          <p>We do not sell personal information and we do not share it for cross‑context behavioral advertising.</p>
        </section>

        {/* Section 5 */}
        <section className="privacy-section">
          <h2>5. Data storage and retention</h2>
          
          <h3>5.1 On‑device storage</h3>
          <p>Calorie Tracker is a local-first app. The App stores data locally in its private storage on your device (including meals, settings, and progress entries). Progress photos saved in the App are stored in the App's private storage and are not stored in your system Photos library unless you explicitly save them there.</p>
          <p>If you delete the App from your device, this local data is removed from your device. Reinstalling the App starts with an empty dataset.</p>
          <p>Your device backups (e.g., iCloud) may include some App data depending on your iOS settings and backup/restore choices.</p>

          <h3>5.2 Server processing and retention</h3>
          <p>We do not store your meal entries, progress data, or progress photos on our servers.</p>
          <p>If you use cloud-powered features, we process your requests through our backend and service providers listed above in order to return results to you. We do not intentionally retain request payloads after processing.</p>
          <p>We may retain <strong>minimal security and operational logs</strong> (e.g., timestamps, IP address, endpoint path, and response codes) for up to <strong>14 days</strong> for reliability, abuse prevention, and debugging.</p>
          <p>To improve performance and reduce upstream requests, we may cache <strong>normalized nutrition lookup queries and results</strong> (e.g., for USDA/Open Food Facts) for up to <strong>7 days</strong>. We do not cache audio recordings or AI prompts.</p>
        </section>

        {/* Section 6 */}
        <section className="privacy-section">
          <h2>6. Security</h2>
          <p>We use technical safeguards designed to protect your information, including encryption in transit (HTTPS). Some sensitive data is stored using iOS protected storage mechanisms (e.g., Keychain for security credentials).</p>
          <p>No method of transmission or storage is 100% secure; we continuously improve our safeguards.</p>
        </section>

        {/* Section 7 */}
        <section className="privacy-section">
          <h2>7. Your choices and rights</h2>

          <h3>7.1 Optional permissions</h3>
          <p>You can use many parts of the App without granting certain permissions:</p>
          <ul>
            <li>Microphone access is optional (you can type instead of dictating).</li>
            <li>Camera/photo access is optional (you can skip label scanning or photos).</li>
          </ul>

          <h3>7.2 Access, deletion, and correction</h3>
          <p>Because the App does not require accounts, most of your data is stored on your device:</p>
          <ul>
            <li>You can delete entries and photos within the App (where available).</li>
            <li>You can remove all local App data by deleting the App from your device.</li>
          </ul>
          <p>If you believe we retain server‑side data related to your use, contact us at <a href="mailto:support@smartkcal.app">support@smartkcal.app</a>.</p>
          <p>You can also contact us if you want more details about our server-side logging and retention.</p>

          <h3>7.3 Region‑specific rights</h3>
          <p>Depending on your location, you may have additional rights (e.g., GDPR/UK GDPR or certain U.S. state privacy laws). We will respond to verified requests as required by applicable law.</p>
        </section>

        {/* Section 8 */}
        <section className="privacy-section">
          <h2>8. Children's privacy</h2>
          <p>The App is not intended for children under <strong>13</strong> (or the minimum age required in your country). We do not knowingly collect personal information from children. If you believe a child has provided personal information, contact us to request deletion.</p>
        </section>

        {/* Section 9 */}
        <section className="privacy-section">
          <h2>9. International data transfers</h2>
          <p>Our service providers may process data in countries other than your own. Where required, we use appropriate safeguards for international transfers.</p>
        </section>

        {/* Section 10 */}
        <section className="privacy-section">
          <h2>10. Changes to this policy</h2>
          <p>We may update this Privacy Policy from time to time. We will revise the "Last updated" date and, where required, provide additional notice.</p>
        </section>

        {/* Section 11 */}
        <section className="privacy-section">
          <h2>11. Contact us</h2>
          <p>Email: <a href="mailto:support@smartkcal.app">support@smartkcal.app</a></p>
        </section>

        {/* Footer */}
        <footer className="privacy-footer">
          <p>© 2026 Calorie Tracker</p>
        </footer>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
